import React, { forwardRef } from 'react';
import { SizeVariants, ThemeVariants, CommonDefaults } from '../../../types/common';
import styles from './Card.module.css';

const Card = forwardRef(({
  children,
  variant = 'default',
  size = CommonDefaults.size,
  padding = 'default',
  shadow = 'default',
  hover = false,
  interactive = false,
  loading = false,
  header,
  footer,
  className,
  style,
  onClick,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
  'data-testid': testId,
  ...props
}, ref) => {
  const isInteractive = interactive || Boolean(onClick);
  
  const cardClasses = [
    styles.card,
    styles[variant],
    styles[size],
    styles[`padding-${padding}`],
    styles[`shadow-${shadow}`],
    hover && styles.hover,
    isInteractive && styles.interactive,
    loading && styles.loading,
    className
  ].filter(Boolean).join(' ');
  
  const cardProps = {
    className: cardClasses,
    style,
    onClick,
    onFocus,
    onBlur,
    'aria-label': ariaLabel,
    'data-testid': testId,
    ref,
    ...(isInteractive && {
      role: 'button',
      tabIndex: 0,
      onKeyDown: (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick(e);
        }
      }
    }),
    ...props
  };
  
  return (
    <div {...cardProps}>
      {loading && (
        <div className={styles.loadingOverlay} aria-hidden="true">
          <div className={styles.loadingSpinner}>
            <svg viewBox="0 0 24 24" className={styles.spinner}>
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
          </div>
        </div>
      )}
      
      {header && (
        <div className={styles.header}>
          {header}
        </div>
      )}
      
      <div className={styles.content}>
        {children}
      </div>
      
      {footer && (
        <div className={styles.footer}>
          {footer}
        </div>
      )}
    </div>
  );
});

Card.displayName = 'Card';

// Compound components for structured card usage
const CardHeader = ({ children, className, ...props }) => (
  <div className={`${styles.cardHeader} ${className || ''}`} {...props}>
    {children}
  </div>
);

const CardContent = ({ children, className, ...props }) => (
  <div className={`${styles.cardContent} ${className || ''}`} {...props}>
    {children}
  </div>
);

const CardFooter = ({ children, className, ...props }) => (
  <div className={`${styles.cardFooter} ${className || ''}`} {...props}>
    {children}
  </div>
);

const CardTitle = ({ children, className, level = 3, ...props }) => {
  const Heading = `h${level}`;
  return (
    <Heading className={`${styles.cardTitle} ${className || ''}`} {...props}>
      {children}
    </Heading>
  );
};

const CardDescription = ({ children, className, ...props }) => (
  <p className={`${styles.cardDescription} ${className || ''}`} {...props}>
    {children}
  </p>
);

// Export compound components
Card.Header = CardHeader;
Card.Content = CardContent; 
Card.Footer = CardFooter;
Card.Title = CardTitle;
Card.Description = CardDescription;

export default Card;